/**
 * shared 스키마 관문 — UI가 @hcltech/shared를 쓰는 유일한 통로.
 *
 * 규칙:
 *  - 반드시 schemas/* 서브패스로만 import (배럴 index는 hono/jwt를 끌고 옴 — UI에 불필요)
 *  - 도메인 api 파일들은 shared를 직접 만지지 말고 여기서 가져갈 것
 *  - 응답 타입은 손으로 정의하지 않는다 — z.infer(shared)가 정본
 *  - 외부 경계(HTTP 응답)는 여기 스키마로 safeParse 1회 — 통과 후엔 신뢰
 *
 * 배선: package.json "@hcltech/shared": github:hm-spt/hcltech-parts#main&path:/packages/shared
 *       (Sola 패턴 — dist 커밋 + github dep. 옆 폴더 clone 전제 없음)
 * 갱신: shared 수정 → parts에서 pnpm --filter @hcltech/shared build → 커밋·push
 *       → ui에서 pnpm update @hcltech/shared
 *
 * ResponseSchema 파생 규칙:
 *  shared의 *Schema(DB row)는 CreateSchema.extend 기반이라 일부 필드가 optional인데,
 *  실제 응답 매퍼(toQuote/toDeal)는 전 필드를 항상 포함(?? null 정규화까지) —
 *  .required()로 조여 "필드가 빠지면 에러"라는 정확한 응답 계약을 만든다.
 */
import { z } from 'zod';
import { QuoteSchema, QuoteItemSchema } from '@hcltech/shared/schemas/quote.schema';
import {
  DealSchema, DealItemSchema, DealConfigurationSchema,
  DealFullThreadSchema, ChatMessagePageSchema,
} from '@hcltech/shared/schemas/deal.schema';
import { ChatThreadSchema, ChatMessageSchema } from '@hcltech/shared/schemas/chat.schema';
import { SupplierSchema } from '@hcltech/shared/schemas/supplier.schema';
import { CustomerSchema } from '@hcltech/shared/schemas/customer.schema';
import { ContactSchema } from '@hcltech/shared/schemas/contact.schema';
import {
  SourcingRfqBubbleSchema, SourcingNeedRowSchema, DealSourcingResponseSchema,
  NeedRfqRowSchema, NeedRfqBatchSchema, NeedRfqsResponseSchema,
  RfqGroupedItemSchema, RfqGroupedRowSchema, RfqListGroupedResponseSchema,
  RfqBatchFromDealItemsResponseSchema,
} from '@hcltech/shared/schemas/sourcing.schema';

// ─── re-export (원본 스키마·상수) ────────────────────────────────────
export {
  QuoteSchema,
  QuoteItemSchema,
  QuoteFullSchema,
  QUOTE_STATUSES,
  QUOTE_RESULTS,
} from '@hcltech/shared/schemas/quote.schema';
export type { QuoteStatus, QuoteResult } from '@hcltech/shared/schemas/quote.schema';

export {
  DealSchema, DealItemSchema, DealConfigurationSchema,
  DealFullThreadSchema, ChatMessagePageSchema,
} from '@hcltech/shared/schemas/deal.schema';

export { ChatThreadSchema, ChatMessageSchema } from '@hcltech/shared/schemas/chat.schema';

// ═══ Quote — 응답 스키마 + 타입 정본 ═══════════════════════════════════
// is_outdated/outdated_parts 는 조회 시점 계산 필드 — 구 백엔드가 안 보내도 파싱 깨지지 않게 optional 유지(.required() 이후 다시 완화).
export const QuoteResponseSchema = QuoteSchema.required().extend({
  is_outdated:    z.boolean().optional(),
  outdated_parts: z.string().nullable().optional(),
});
export const QuoteFullResponseSchema = QuoteResponseSchema.extend({
  items: z.array(QuoteItemSchema),
});
export const DealQuotesResponseSchema = z.object({
  quotes: z.array(QuoteResponseSchema),
});

export type Quote = z.infer<typeof QuoteResponseSchema>;
export type QuoteItem = z.infer<typeof QuoteItemSchema>;
export type QuoteFull = z.infer<typeof QuoteFullResponseSchema>;

// ═══ Deal — 응답 스키마 + 타입 정본 ════════════════════════════════════
// toDeal이 조인 컬럼을 ?? null, is_watched를 ?? false로 정규화 — 응답은 전 필드 항상 존재.
export const DealResponseSchema = DealSchema.required();
export const DealFullResponseSchema = DealResponseSchema.extend({
  items:          z.array(DealItemSchema),
  configurations: z.array(DealConfigurationSchema),
  threads:        z.array(DealFullThreadSchema),
});
export const DealListResponseSchema = z.object({
  rows:     z.array(DealResponseSchema),
  total:    z.number().int(),
  page:     z.number().int(),
  pageSize: z.number().int(),
});

// 소형 응답 (shared에 스키마 없음 — 로컬 정의. 도메인 커지면 shared 이관 후보)
export const IdResponseSchema = z.object({ id: z.string() });
export const OkResponseSchema = z.object({ ok: z.boolean() });
export const DealItemsReplaceResponseSchema = z.object({ items: z.array(DealItemSchema) });
export const DealStatusHistoryResponseSchema = z.object({
  history: z.array(z.object({ status: z.string(), changed_at: z.string() })),
});
export const DealEventSchema = z.object({
  id:         z.string(),
  kind:       z.string(),
  message:    z.string(),
  ref_type:   z.string().nullable(),
  ref_id:     z.string().nullable(),
  created_at: z.string(),
});
export const DealEventsResponseSchema = z.object({ events: z.array(DealEventSchema) });

export type DealRow = z.infer<typeof DealResponseSchema>;
export type DealItem = z.infer<typeof DealItemSchema>;
export type DealConfiguration = z.infer<typeof DealConfigurationSchema>;
export type DealFull = z.infer<typeof DealFullResponseSchema>;
export type DealStatus = DealRow['status'];
export type DealClassification = NonNullable<DealRow['classification']>;
export type DealStatusHistoryEntry = z.infer<typeof DealStatusHistoryResponseSchema>['history'][number];
export type DealEvent = z.infer<typeof DealEventSchema>;

// ═══ Chat — 응답 스키마 + 타입 정본 ════════════════════════════════════
// POST /deal/:id/chat 응답 — shared에 스키마 없음(admin 전용) — 로컬 정의
export const DealChatResponseSchema = z.object({
  user_message:      ChatMessageSchema,
  assistant_message: ChatMessageSchema,
  audit_metadata:    z.record(z.string(), z.unknown()).nullable(),
});

export type ChatThread = z.infer<typeof ChatThreadSchema>;
export type ChatMessage = z.infer<typeof ChatMessageSchema>;
export type DealFullThread = z.infer<typeof DealFullThreadSchema>;
export type ChatMessagePage = z.infer<typeof ChatMessagePageSchema>;
export type DealChatResponse = z.infer<typeof DealChatResponseSchema>;

// ═══ Sourcing — 응답 스키마 + 타입 정본 ════════════════════════════════
export {
  SourcingRfqBubbleSchema, SourcingNeedRowSchema, NeedCreateResponseSchema, DealSourcingResponseSchema,
  NeedRfqRowSchema, NeedRfqBatchSchema, NeedRfqsResponseSchema,
  RfqGroupedItemSchema, RfqGroupedRowSchema, RfqListGroupedResponseSchema,
  RfqBatchFromDealItemsResponseSchema,
  SALES_REQUEST_TAGS, SOURCING_MEMO_TAGS,
} from '@hcltech/shared/schemas/sourcing.schema';
export type {
  RfqBatchFromDealItemsResultRow, RfqBatchFromDealItemsSkipped, NeedCreateResponse,
  // 니즈 생성 입력 — sourcing-api 가 손으로 복제하던 것을 정본으로 되돌렸다
  // (복제본이 있으면 shared 에 필드를 추가해도 UI 는 모른 채 컴파일만 깨진다).
  NeedCreateInput,
} from '@hcltech/shared/schemas/sourcing.schema';

// 소싱 페이지 확장 행/목록 (shared에 없는 admin 전용 응답 — shared 파생으로 정의)
export const SourcingListNeedSchema = SourcingNeedRowSchema.extend({
  deal_name:          z.string().nullable(),
  sourcing_group_key: z.string().nullable(),   // 같은 값끼리 한 묶음. NULL=단독
  sourcing_sort_at:   z.string().nullable(),   // 수동 정렬 ts. NULL=created_at
});
export const SourcingCursorSchema = z.object({ at: z.string(), id: z.string() });
export const SourcingListResponseSchema = z.object({
  needs:      z.array(SourcingListNeedSchema),
  nextCursor: SourcingCursorSchema.nullable(),
});
export const NeedPurchaseRequestResponseSchema = z.object({
  id:                 z.coerce.number(),
  purchase_requested: z.boolean(),
});
// 매입처 지정 모달 — 기존 fetchActiveSuppliers의 손 정규화(Number/String)를 coerce가 대신
export const SupplierPickSchema = z.object({
  id:         z.coerce.number(),
  name:       z.string(),
  country:    z.string().nullable(),
  categories: z.array(z.string()),
  total_rfq:  z.coerce.number(),
  priced_rfq: z.coerce.number(),
});
export const SourcingSuppliersResponseSchema = z.object({ suppliers: z.array(SupplierPickSchema) });
export const SyncClusterResponseSchema = z.object({
  group_code:     z.string(),
  need_count:     z.number(),
  supplier_count: z.number(),
  added:          z.number(),
  removed:        z.number(),
});
// 레거시 RFQ(리드 흐름) — 응답이 raw row 덩어리라 핵심 필드만 고정, 나머지는 통과
export const RfqBulkResponseSchema = z.object({
  rfq:    z.object({ id: z.coerce.number() }).catchall(z.unknown()),
  quotes: z.array(z.record(z.string(), z.unknown())),
});
export const RfqFullResponseSchema = z.object({
  rfq:             z.object({ id: z.coerce.number() }).catchall(z.unknown()),
  source_supplier: z.record(z.string(), z.unknown()).nullable(),
  quotes:          z.array(z.record(z.string(), z.unknown())),
});

export type SourcingRfqBubble = z.infer<typeof SourcingRfqBubbleSchema>;
export type SourcingNeedRow = z.infer<typeof SourcingNeedRowSchema>;
export type DealSourcingResponse = z.infer<typeof DealSourcingResponseSchema>;
export type SourcingListNeed = z.infer<typeof SourcingListNeedSchema>;
export type SourcingCursor = z.infer<typeof SourcingCursorSchema>;
export type SourcingListResponse = z.infer<typeof SourcingListResponseSchema>;
export type NeedRfqRow = z.infer<typeof NeedRfqRowSchema>;
export type NeedRfqBatch = z.infer<typeof NeedRfqBatchSchema>;
export type NeedRfqsResponse = z.infer<typeof NeedRfqsResponseSchema>;
export type RfqGroupedItem = z.infer<typeof RfqGroupedItemSchema>;
export type RfqGroupedRow = z.infer<typeof RfqGroupedRowSchema>;
export type RfqListGroupedResponse = z.infer<typeof RfqListGroupedResponseSchema>;
export type RfqBatchFromDealItemsResponse = z.infer<typeof RfqBatchFromDealItemsResponseSchema>;
export type SupplierPick = z.infer<typeof SupplierPickSchema>;
export type SyncClusterResponse = z.infer<typeof SyncClusterResponseSchema>;
export type RfqBulkResponse = z.infer<typeof RfqBulkResponseSchema>;
export type RfqFullResponse = z.infer<typeof RfqFullResponseSchema>;

// ═══ Partner (supplier·customer·contact) — 응답 스키마 + 타입 정본 ═══════
// UI 소비처가 id를 number로 쓰는 계약 — 기존 손 정규화(Number(r.id)) 대신
// z.coerce.number()가 검증+변환을 한 번에 (shared row는 id string).
export const SupplierRowSchema = SupplierSchema.required().extend({
  id: z.coerce.number(),
});
export const SupplierSearchResponseSchema = z.object({ rows: z.array(SupplierRowSchema) });
export const SupplierCreateResponseSchema = z.union([
  z.object({ row: SupplierRowSchema }),
  z.object({ rows: z.array(SupplierRowSchema) }),
]);

export const CustomerRowSchema = CustomerSchema.required().extend({
  id: z.coerce.number(),
});
export const CustomerSearchResponseSchema = z.object({ rows: z.array(CustomerRowSchema) });

export const ContactResponseSchema = ContactSchema.required();
export const ContactsResponseSchema = z.object({ rows: z.array(ContactResponseSchema) });

// 레거시 generic 테이블 응답 (GET /api/admin/table/:name) — 느슨한 경계
export const RowsResponseSchema = z.object({
  rows:   z.array(z.record(z.string(), z.unknown())),
  total:  z.number(),
  limit:  z.number(),
  offset: z.number(),
  // 백엔드 응답(AdminTableListResponse)엔 sort/order 없음 → optional (없어도 통과).
  sort:   z.string().optional(),
  order:  z.enum(['asc', 'desc']).optional(),
});

export type SupplierRow = z.infer<typeof SupplierRowSchema>;
export type CustomerRow = z.infer<typeof CustomerRowSchema>;
export type Contact = z.infer<typeof ContactResponseSchema>;

// ═══ Catalog·검색·미등록·기타 — 응답 스키마 (shared에 없는 admin 전용) ═══
export const TablesResponseSchema = z.object({
  tables: z.array(z.object({
    name:        z.string(),
    label:       z.string(),
    group:       z.string(),
    searchable:  z.array(z.string()),
    filterable:  z.array(z.string()),
    sortable:    z.array(z.string()),
    defaultSort: z.object({ col: z.string(), order: z.enum(['asc', 'desc']) }),
  })),
});
// 검색 응답 — 정본은 shared. 최상위 inventory/quotes 배열 없음(재고는 각 행 per-row).
//   과거 로컬 복제본이 phantom inventory/quotes[]를 요구해 dev 검색이 깨졌던 것을 통합 교정.
export {
  CatalogSearchRowSchema as CatalogSearchHitSchema,
  SearchResponseSchema as CatalogSearchResponseSchema,
} from '@hcltech/shared/schemas/search.schema';
export const PendingPartSchema = z.object({
  id:             z.string(),
  option_part:    z.string().nullable(),   // 307: 번호를 모르면 NULL (331 이 PN 아닌 값을 비운다)
  description:    z.string().nullable(),
  catalog_table_name: z.string().nullable(),
  status:         z.string(),
  source_deal_id: z.string().nullable(),
  created_at:     z.string(),
  updated_at:     z.string(),
});
export const PendingPartsResponseSchema = z.object({ rows: z.array(PendingPartSchema) });
export const TodayExchangeRateSchema = z.object({
  rate_krw_per_usd: z.number(),
  date:             z.string(),
  source:           z.string(),
});

export type PendingPart = z.infer<typeof PendingPartSchema>;
export type TodayExchangeRate = z.infer<typeof TodayExchangeRateSchema>;

// ═══ 임대 계산식 프리셋 (마이그 177/180) ══════════════════════════════
// 시트 `임대 견적 계산기 v.0813` 의 기본 정책·추가 정책 두 표가 이 모양이다.
// 응답 스키마는 shared 가 정본 — 백엔드 서비스도 같은 타입을 반환한다.
//
// ★ 계산 함수(computeRentalPricing / computeRentalPenalty)와 상수
//   (RENTAL_COST_KINDS)는 스키마가 아니라 순수 함수라 여기 두지 않는다.
//   화면이 `@hcltech/shared/rental/rental-pricing` 딥 경로로 직접 가져간다
//   (server-config/shorthand·config-name 과 같은 방식).
export {
  RentalPresetSchema, RentalPresetItemSchema, RentalPresetListResponseSchema,
  RENTAL_PRESET_DEFAULT_FOR,
  toRentalPolicy, toRentalCostItems,
} from '@hcltech/shared/schemas/rental-preset.schema';
export type {
  RentalPreset, RentalPresetItem, RentalPresetDefaultFor,
} from '@hcltech/shared/schemas/rental-preset.schema';
export type { RentalCostKind } from '@hcltech/shared/rental/rental-pricing';
